import type { CompanyType } from '@crm/entities/contact';
import { companyType } from '@crm/entities/contact';
import { contactSortFields } from '@travel-planner/shared/schemas/contact';

const companyTypeValues = Object.values(companyType) as readonly string[];

// `SortField` is derived from the shared backend enum so the two never
// drift apart.
type SortField = (typeof contactSortFields)[number];

const sortOrderValues = ['asc', 'desc'] as const;
type SortOrder = (typeof sortOrderValues)[number];

export type ContactsSearch = {
  contactName?: string;
  companyName?: string;
  companyType?: CompanyType[];
  organizationIds?: string[];
  page?: number;
  pageSize?: number;
  sortBy?: SortField;
  sortOrder?: SortOrder;
};

const asCompanyTypeArray = (v: unknown): CompanyType[] | undefined =>
  Array.isArray(v)
    ? v.filter(
        (x): x is CompanyType =>
          typeof x === 'string' && companyTypeValues.includes(x)
      )
    : undefined;

const asIdArray = (v: unknown): string[] | undefined =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === 'string')
    : undefined;

const asSortField = (v: unknown): SortField | undefined =>
  typeof v === 'string' && (contactSortFields as readonly string[]).includes(v)
    ? (v as SortField)
    : undefined;

const asSortOrder = (v: unknown): SortOrder | undefined =>
  typeof v === 'string' && (sortOrderValues as readonly string[]).includes(v)
    ? (v as SortOrder)
    : undefined;

export const validateContactsSearch = (
  input: Record<string, unknown>
): ContactsSearch => ({
  contactName:
    typeof input.contactName === 'string' ? input.contactName : undefined,
  companyName:
    typeof input.companyName === 'string' ? input.companyName : undefined,
  companyType: asCompanyTypeArray(input.companyType),
  organizationIds: asIdArray(input.organizationIds),
  page: typeof input.page === 'number' ? input.page : undefined,
  pageSize: typeof input.pageSize === 'number' ? input.pageSize : undefined,
  sortBy: asSortField(input.sortBy),
  sortOrder: asSortOrder(input.sortOrder),
});
